const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api'
).replace(/\/+$/, '');

const ACCESS_TOKEN_KEY = 'votum:access-token';
const REFRESH_TOKEN_KEY = 'votum:refresh-token';

export const tokenStorage = {
  getAccess: () => localStorage.getItem(ACCESS_TOKEN_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  set({ access, refresh }) {
    if (access) localStorage.setItem(ACCESS_TOKEN_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

export class ApiError extends Error {
  constructor(message, status = 0, data = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    this.code = data?.code || 'API_ERROR';
  }
}

const parseResponse = async (response) => {
  if (response.status === 204) return null;
  const contentType = response.headers.get('content-type') || '';
  return contentType.includes('application/json')
    ? response.json()
    : response.text();
};

let refreshPromise = null;

const refreshAccessToken = async () => {
  const refresh = tokenStorage.getRefresh();
  if (!refresh) throw new ApiError('La sesión ha vencido.', 401);
  if (!refreshPromise) {
    refreshPromise = fetch(`${API_BASE_URL}/auth/token/refresh/`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh }),
    })
      .then(async (response) => {
        const data = await parseResponse(response);
        if (!response.ok || !data?.access) {
          throw new ApiError('La sesión ha vencido.', response.status, data);
        }
        tokenStorage.set({ access: data.access, refresh: data.refresh || refresh });
        return data.access;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

const buildUrl = (path, params) => {
  const url = new URL(`${API_BASE_URL}/${String(path).replace(/^\/+/, '')}`);
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== '' && value !== null && value !== undefined) {
      url.searchParams.set(key, value);
    }
  });
  return url.toString();
};

const request = async (
  method,
  path,
  { body, params, headers = {}, signal, retry = true, authenticated = true } = {}
) => {
  const access = authenticated ? tokenStorage.getAccess() : null;
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  let response;
  try {
    response = await fetch(buildUrl(path, params), {
      method,
      signal,
      headers: {
        Accept: 'application/json',
        ...(body !== undefined && !isFormData
          ? { 'Content-Type': 'application/json' }
          : {}),
        ...(access ? { Authorization: `Bearer ${access}` } : {}),
        ...headers,
      },
      ...(body !== undefined
        ? { body: isFormData ? body : JSON.stringify(body) }
        : {}),
    });
  } catch (error) {
    if (error.name === 'AbortError') throw error;
    throw new ApiError(
      'No fue posible comunicarse con la API.',
      0,
      { detail: error.message }
    );
  }
  if (response.status === 401 && authenticated && retry) {
    try {
      await refreshAccessToken();
      return request(method, path, {
        body, params, headers, signal, retry: false, authenticated,
      });
    } catch (error) {
      tokenStorage.clear();
      window.dispatchEvent(new Event('votum:session-expired'));
      throw error;
    }
  }
  const data = await parseResponse(response);
  if (!response.ok) {
    const errors = data?.errors || data;
    const firstError = errors && typeof errors === 'object'
      ? Object.values(errors).flat()[0]
      : null;
    throw new ApiError(
      data?.message || firstError || data?.detail ||
        `La API respondió con estado ${response.status}.`,
      response.status,
      data
    );
  }
  return data;
};

const download = async (path, { params, accept = 'application/octet-stream', retry = true } = {}) => {
  const access = tokenStorage.getAccess();
  const response = await fetch(buildUrl(path, params), {
    headers: {
      Accept: `${accept}, application/json;q=0.5`,
      ...(access ? { Authorization: `Bearer ${access}` } : {}),
    },
  });
  if (response.status === 401 && retry) {
    try {
      await refreshAccessToken();
      return download(path, { params, accept, retry: false });
    } catch (error) {
      tokenStorage.clear();
      window.dispatchEvent(new Event('votum:session-expired'));
      throw error;
    }
  }
  if (!response.ok) {
    const data = await parseResponse(response);
    throw new ApiError(
      data?.message || data?.detail || 'No fue posible exportar el reporte.',
      response.status,
      data
    );
  }
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    const data = await response.json();
    throw new ApiError(
      data?.message || data?.detail || 'El servidor no generó el archivo solicitado.',
      response.status,
      data
    );
  }
  return {
    blob: await response.blob(),
    disposition: response.headers.get('content-disposition') || '',
    contentType,
  };
};

export const apiClient = Object.freeze({
  baseUrl: API_BASE_URL,
  get: (path, options) => request('GET', path, options),
  post: (path, body, options) => request('POST', path, { ...options, body }),
  put: (path, body, options) => request('PUT', path, { ...options, body }),
  patch: (path, body, options) => request('PATCH', path, { ...options, body }),
  delete: (path, options) => request('DELETE', path, options),
  download,
});
